import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();
const USER_STORAGE_KEY = 'authUser';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await authAPI.checkAuth();
      if (response.data.success && response.data.authenticated) {
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);
        setUser(storedUser ? JSON.parse(storedUser) : { authenticated: true });
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.log('Not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, turnstileToken) => {
    try {
      const response = await authAPI.login(email, turnstileToken);
      if (response.data.success) {
        toast.success('OTP sent to your email');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verifyOTP = async (otp) => {
    try {
      const response = await authAPI.verifyOTP(otp);
      if (response.data.success) {
        setUser(response.data.user);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(response.data.user || {}));
        setIsAuthenticated(true);
        toast.success('Login successful');
        return { success: true, user: response.data.user };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'OTP verification failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem(USER_STORAGE_KEY);
      toast.success('Logged out successfully');
    } catch (error) {
      console.log('Logout error:', error);
      // Even if logout fails, clear local state
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    verifyOTP,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
